// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * Help Translations Validator
 * @file validateTranslations.js
 * @description Validación automática de claves de traducción requeridas
 * @version v3.2.0
 */

import {
  HELP_PLANS_REQUIRED_KEYS,
  HELP_PLANS_REQUIRED_KEYS_COUNT,
  getMissingKeys,
  getExtraKeys,
  getEmptyKeys,
  validateKeysMatchSchema
} from '../../../i18n/help/help.plans.schema.js';

/**
 * @deprecated Usar HELP_PLANS_REQUIRED_KEYS del schema
 * Mantenido para compatibilidad
 */
export const REQUIRED_PLAN_KEYS = [...HELP_PLANS_REQUIRED_KEYS];

/**
 * Valida que un objeto de traducciones contenga TODAS las claves requeridas
 * 
 * @param {Object} translations - Objeto de traducciones a validar
 * @param {string} language - Código de idioma (para logging)
 * @param {boolean} strictMode - Si es true, lanza error en consola. Si es false, solo warning
 * @returns {Object} - { isValid: boolean, missingKeys: string[], emptyKeys: string[] }
 */
export const validatePlanTranslations = (translations, language = 'unknown', strictMode = true) => {
  if (!translations || typeof translations !== 'object') {
    const error = `❌ [I18N_VALIDATOR] Invalid translations object for language "${language}"`;
    if (strictMode) {
      console.error(error);
    } else {
      console.warn(error);
    }
    return {
      isValid: false,
      missingKeys: REQUIRED_PLAN_KEYS,
      emptyKeys: [],
      error: 'Invalid translations object'
    };
  }

  // Usar funciones del schema para validación
  const missingKeys = getMissingKeys(translations);
  const emptyKeys = getEmptyKeys(translations);
  const extraKeys = getExtraKeys(translations);

  const isValid = missingKeys.length === 0 && emptyKeys.length === 0 && extraKeys.length === 0;

  if (!isValid) {
    const errorDetails = {
      language,
      missingCount: missingKeys.length,
      emptyCount: emptyKeys.length,
      extraCount: extraKeys.length,
      missingKeys: missingKeys.length > 0 ? missingKeys : undefined,
      emptyKeys: emptyKeys.length > 0 ? emptyKeys : undefined,
      extraKeys: extraKeys.length > 0 ? extraKeys : undefined
    };

    // Log detallado de cada error
    if (missingKeys.length > 0) {
      missingKeys.forEach(key => {
        console.error(`❌ [I18N] Missing key: ${key} (lang: ${language})`);
      });
    }
    
    if (emptyKeys.length > 0) {
      emptyKeys.forEach(key => {
        console.error(`❌ [I18N] Empty key: ${key} (lang: ${language})`);
      });
    }
    
    if (extraKeys.length > 0) {
      extraKeys.forEach(key => {
        console.error(`❌ [I18N] Extra key (not in schema): ${key} (lang: ${language})`);
      });
    }

    if (strictMode) {
      console.error(`❌ [I18N_VALIDATOR] Translation validation FAILED for language "${language}":`, errorDetails);
    } else {
      console.warn(`⚠️ [I18N_VALIDATOR] Translation validation WARNING for language "${language}":`, errorDetails);
    }
  } else {
    console.log(`✅ [I18N_VALIDATOR] Translation validation PASSED for language "${language}" (${HELP_PLANS_REQUIRED_KEYS_COUNT} keys)`);
  }

  return {
    isValid,
    missingKeys,
    emptyKeys,
    extraKeys,
    totalRequired: HELP_PLANS_REQUIRED_KEYS_COUNT,
    found: HELP_PLANS_REQUIRED_KEYS_COUNT - missingKeys.length - emptyKeys.length
  };
};

/**
 * Valida traducciones para múltiples idiomas
 * 
 * @param {Object} translationsByLanguage - Objeto con estructura { 'en': {...}, 'es': {...}, ... }
 * @param {boolean} strictMode - Si es true, lanza error en consola
 * @returns {Object} - Resumen de validación por idioma
 */
export const validateAllLanguages = (translationsByLanguage, strictMode = true) => {
  const results = {};
  let allValid = true;

  Object.keys(translationsByLanguage).forEach(lang => {
    const result = validatePlanTranslations(translationsByLanguage[lang], lang, strictMode);
    results[lang] = result;
    if (!result.isValid) {
      allValid = false;
    }
  });

  if (allValid) {
    console.log(`✅ [I18N_VALIDATOR] All languages validated successfully (${Object.keys(results).length} languages)`);
  } else {
    const failedLanguages = Object.keys(results).filter(lang => !results[lang].isValid);
    console.error(`❌ [I18N_VALIDATOR] Validation failed for ${failedLanguages.length} language(s):`, failedLanguages);
  }

  return {
    allValid,
    results,
    summary: {
      totalLanguages: Object.keys(results).length,
      validLanguages: Object.keys(results).filter(lang => results[lang].isValid).length,
      failedLanguages: Object.keys(results).filter(lang => !results[lang].isValid)
    }
  };
};

/**
 * Función helper para validar traducciones después de cargarlas
 * Útil para usar en componentes React después de await loadHelpTranslations()
 * 
 * @param {Object} translations - Traducciones cargadas
 * @param {string} currentLanguage - Idioma actual
 * @returns {boolean} - true si es válido, false si hay problemas
 */
export const validateLoadedTranslations = (translations, currentLanguage) => {
  const result = validatePlanTranslations(translations, currentLanguage, true);
  return result.isValid;
};
